import { useLayoutEffect, useMemo, useRef } from 'react'
import { useLatest } from 'react-use'
import { v4 } from 'uuid'
import { SmartSuggestionStoreItem, useControlStore } from '@editor/stores'
import { useAppStore } from '@stores'

export interface HTMLSuggestion {
  type: 'html-suggestion'
  text: string
  onAccept: () => void
  onAbort?: () => void
  enabled: boolean
}

export interface ScenePointSuggestion {
  type: 'scene-point-suggestion'
  text: string
  point: { x: number; y: number; z: number }
  onAccept: () => void
  onAbort?: () => void
  enabled: boolean
}

interface NoneSuggestion {
  type: 'none-suggestion'
}

const useSmartSuggestion = (options: HTMLSuggestion | ScenePointSuggestion | null): boolean => {
  const idRef = useRef(v4())
  const onAcceptRef = useLatest(options?.onAccept)
  const onAbortRef = useLatest(options?.onAbort)
  const smartSuggestionsEnabled = useAppStore(state => state.smartSuggestionsEnabled)
  const effectiveEnabled = !!options?.enabled && smartSuggestionsEnabled
  const addSmartSuggestion = useControlStore(state => state.addSmartSuggestion)
  const removeSmartSuggestion = useControlStore(state => state.removeSmartSuggestion)
  const storeEnabled = useControlStore(
    state => state.smartSuggestions.find(suggestion => suggestion.id === idRef.current)?.enabled,
  )

  const parsedOption: HTMLSuggestion | ScenePointSuggestion | NoneSuggestion = useMemo(
    () =>
      options ?? {
        type: 'none-suggestion',
      },
    [options],
  )

  useLayoutEffect(() => {
    if (parsedOption.type === 'none-suggestion') return

    const id = idRef.current

    const suggestion: SmartSuggestionStoreItem =
      parsedOption.type === 'html-suggestion'
        ? {
            id,
            type: 'html-suggestion',
            text: parsedOption.text,
            callback: () => onAcceptRef.current?.(),
            onAbort: () => onAbortRef.current?.(),
            enabled: effectiveEnabled,
          }
        : {
            id,
            type: 'scene-point-suggestion',
            text: parsedOption.text,
            point: parsedOption.point,
            callback: () => onAcceptRef.current?.(),
            onAbort: () => onAbortRef.current?.(),
            enabled: effectiveEnabled,
          }
    addSmartSuggestion(suggestion)

    return () => {
      removeSmartSuggestion(id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    addSmartSuggestion,
    effectiveEnabled,
    onAbortRef,
    onAcceptRef,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    'point' in parsedOption ? parsedOption.point : undefined,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    'text' in parsedOption ? parsedOption.text : undefined,
    parsedOption.type,
    removeSmartSuggestion,
  ])

  return !!storeEnabled
}

export default useSmartSuggestion
